import React, { useCallback, useRef } from 'react';
import ModalWrapper from 'src/components/common/modal/ModalWrapper';
import { blueButtonClass } from 'src/components/common/common';
import { InspectTable_PRD3010_R01 } from './InspectTable_PRD3010_R01';
import { usePRD3010_R01Store } from './Store_PRD3010_R01';
import { AgGridReact } from 'ag-grid-react';
import { PRD3010_R01DowntimeDto, PRD3010_R01HeaderDto, PRD3010_R01InspectDto } from './Dto_PRD3010_R01';
import { useMouseContent } from 'src/utils/mouse/mouseContent';
import { showWarning } from 'src/utils/toast/toastUtil';
import { InputForm } from 'src/components/common/form/input/InputForm';
import { validateEditBadQtyAgainstInspectList } from './inspectBadQtyUtil_PRD3010_R01';

interface Props {
	defaultPRD3010_R01HeaderRow: PRD3010_R01HeaderDto;
    inspectGridRef: React.RefObject<AgGridReact<PRD3010_R01InspectDto> | null>;
	defaultPRD3010_R01InspectRow: PRD3010_R01InspectDto;
    handleDeleteClick: () => void;
}

export const Modal_PRD3010_R01 = ({
	defaultPRD3010_R01HeaderRow,
    inspectGridRef,
    defaultPRD3010_R01InspectRow,
    handleDeleteClick,
}: Props) => {
    const { 
		modalVisible, setModalVisible,
		editPRD3010_R01, setEditPRD3010_R01,
		PRD3010_R01InspectList,
		deletedPRD3010_R01InspectList,
	 } = usePRD3010_R01Store();

    const modalRef = useRef<HTMLDivElement>(null);
    const modalInfoRef = useRef<HTMLDivElement>(null);
    const isResizing_modal = useRef(false);

    const RESIZER_MODAL = {
        code: 'PRD3010_R01_modal',
        type: 'width',
        storageKey: 'PRD3010_R01-modal-split-width',
        min: 5,
        max: 80,
        default: 50
    } as const;

    const {
        splitValue: splitValue_modal,
        handleMouseDown: handleMouseDown_modal,
    } = useMouseContent({
        resizer: RESIZER_MODAL,
        isResizing: isResizing_modal,
        containerRef: modalRef,
        infoRef: modalRef
    });

	const handleBadQtyChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target;
		setEditPRD3010_R01({
			...defaultPRD3010_R01HeaderRow,
			...editPRD3010_R01,
			[name]: value,
			badQty: Number(value) || 0
		});
	}, [editPRD3010_R01, defaultPRD3010_R01HeaderRow, setEditPRD3010_R01]);

	const validateInspectBadQty = useCallback(() => {
		const deletedInspectListToNumber = deletedPRD3010_R01InspectList.map(value => Number(value));
		const result = validateEditBadQtyAgainstInspectList(
			editPRD3010_R01?.badQty || 0,
			PRD3010_R01InspectList,
			deletedInspectListToNumber,
		);

		if (!result.valid) {
			showWarning(result.message!);
			return false;
		}
		return true;
	}, [editPRD3010_R01?.badQty, deletedPRD3010_R01InspectList, PRD3010_R01InspectList]);

	const handleConfirmClick = useCallback(() => {
		if (!validateInspectBadQty()) return;
		setModalVisible(false);
	}, [validateInspectBadQty, setModalVisible]);

	const handleCancelClick = useCallback(() => {
		if (!validateInspectBadQty()) return;
		setModalVisible(false);
	}, [validateInspectBadQty, setModalVisible]);

    if (!modalVisible) return null;

    return (
        <ModalWrapper
            size={'qhmd'}
            width={600}
            overlay={true}
            zIndex={10040}
            onOverlayClick={handleCancelClick}
            onEscKeyDown={handleCancelClick}
        >
            <div className="w-full h-[10%] flex items-center px-[24px] bg-gray-100">
                <p className="font-bold">
                    불량 내용 등록
                </p>
            </div>
            <div className="w-full h-[10%] flex items-center px-[24px] gap-[8px]">
                <button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
                    onClick={handleConfirmClick}
                    disabled={false}
                >
                    확인
                </button>
                <button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
                    onClick={handleCancelClick}
                    disabled={false}
                >
                    취소
                </button>
                <button
                    className={`${blueButtonClass} flex-shrink-0 text-white font-semibold px-4  rounded shadow h-[30px]`}
                    onClick={handleDeleteClick}
                    disabled={false}
                >
                    삭제
                </button>
				<InputForm title="불량수량" value={editPRD3010_R01?.badQty || 0}
						editable={true}
						name="badQty"
						onChange={handleBadQtyChange}
						dragSelectAll={true}
						wrapperOption={{
							wrapperStyle: "w-[300px] min-w-[300px] mr-[10px] mb-[4px]",
							titleStyle: "w-[100px] min-w-[100px] text-left",
							inputStyle: "w-[190px] min-w-[190px]",
						}}
					/>
            </div>
            <div className="w-full h-[75%] flex px-[24px] pt-[2px] pb-[12px] gap-[8px]" ref={modalRef}>
                <div className="h-full" style={{ width: `100%` }} ref={modalInfoRef}>
                    <InspectTable_PRD3010_R01 gridRef={inspectGridRef} defaultPRD3010_R01InspectRow={defaultPRD3010_R01InspectRow} />
                </div>
            </div>
        </ModalWrapper>
    );
};

